import React from 'react';
import { FlexDiv, ThemedButton } from '@awarego-ui/components';
import { observer } from 'mobx-react';
import { Box, Chip, CircularProgress, List, ListItem, Typography } from '@mui/material';
import { formatDate } from '../../utils/helpers';
import ErrorMessage from '../errormessage';

function PartnerDuesModalContent({ entry, onClick, updatingRecordKeys = [], updatingVoidKeys = [], error }) {
    if (!entry) return null;

    const getUsdAmountDue = (inv) =>
        inv.currency_code !== 'USD'
            ? (inv.amount_due / inv.exchange_rate) / 100
            : inv.amount_due / 100;

    return (
        <FlexDiv flexDirection="column" gap={2} p={2}>
            <ErrorMessage error={error}  errorType="error"/>
            {entry.due_invoices && entry.due_invoices.length > 0 ? (
                <List sx={{ width: '100%' }}>
                    {entry.due_invoices.map((inv) => {
                        const isUpdatingRecord = Array.isArray(updatingRecordKeys) && (updatingRecordKeys.includes(inv.id) || updatingRecordKeys.includes(String(inv.id)));
                        const isUpdatingVoid = Array.isArray(updatingVoidKeys) && (updatingVoidKeys.includes(inv.id) || updatingVoidKeys.includes(String(inv.id)));

                        return (
                            <ListItem
                                key={inv.id}
                                divider
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    py: 3,
                                    gap: 2,
                                }}
                            >
                                <Box flex="1" minWidth={0}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight: 500,
                                            mb: 2,
                                            '& a': {
                                                color: 'inherit',
                                                textDecoration: 'none',
                                                transition: 'color 0.15s ease, text-decoration 0.15s ease',
                                                '&:hover': {
                                                    color: 'primary.main',
                                                    textDecoration: 'underline',
                                                },
                                            },
                                        }}
                                    >
                                        <a href={`https://awarego.chargebee.com/d/invoices/${inv.id}`} target="_blank" rel="noreferrer">
                                            Invoice #{inv.id} — {getUsdAmountDue(inv).toFixed(2)} USD
                                            {inv.currency_code !== 'USD' ? ` ( ${(inv.amount_due / 100).toFixed(2)} ${inv.currency_code} )` : ''}
                                        </a>
                                    </Typography>

                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Chip
                                            label={`Due: ${formatDate(inv.due_date * 1000)}`}
                                            variant="outlined"
                                            size="small"
                                            sx={{ borderColor: '#ccc', color: '#666', fontSize: '0.75rem', height: 22, marginRight: '0.5rem' }}
                                        />
                                        <Chip
                                            label={inv.status}
                                            variant="outlined"
                                            size="small"
                                            sx={{ borderColor: '#ddd', color: inv.status === 'payment_due' ? '#c55' : '#666', fontSize: '0.75rem', height: 22, marginRight: '0.5rem' }}
                                        />
                                    </Box>
                                </Box>

                                <Box display="flex" flexDirection="column" gap={1} alignItems="flex-start">
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <ThemedButton
                                            mode="secondary"
                                            size="small"
                                            disabled={isUpdatingRecord || isUpdatingVoid || inv.status === 'paid' || inv.status === 'voided'}
                                            text={isUpdatingRecord ? 'Recording...' : 'Record payment'}
                                            onClick={() =>
                                                onClick({ partnerId: entry.id, invoiceId: inv.id, type: 'record' })
                                            }
                                            style={{ minWidth: 140, justifyContent: 'center' }}
                                        />
                                        {isUpdatingRecord && <CircularProgress size={18} sx={{ ml: 1 }} />}
                                    </Box>

                                    <Box display="flex" alignItems="center" gap={1}>
                                        <ThemedButton
                                            mode="gray"
                                            size="small"
                                            disabled={isUpdatingRecord || isUpdatingVoid || inv.status === 'paid' || inv.status === 'voided'}
                                            text={isUpdatingVoid ? 'Voiding...' : 'Void invoice'}
                                            onClick={() =>
                                                onClick({ partnerId: entry.id, invoiceId: inv.id, type: 'void' })
                                            }
                                            style={{ minWidth: 140, justifyContent: 'center' }}
                                        />
                                        {isUpdatingVoid && <CircularProgress size={18} sx={{ ml: 1 }} />}
                                    </Box>
                                </Box>

                            </ListItem>
                        );
                    })}
                </List>
            ) : (
                <Typography>No due invoices.</Typography>
            )}
        </FlexDiv>
    );
}

export default observer(PartnerDuesModalContent);